/**
 * Merges multiple React refs (callback refs and object refs) into a single ref callback.
 * This is useful when you need to assign the same DOM element or component instance
 * to multiple refs at the same time (e.g., an internal ref and an external ref).
 *
 * @typeParam T - The type of the element or component instance being referenced.
 * @param refs - An array of refs, which can be object refs created with `useRef()` or callback refs (functions).
 * @returns A single ref callback function that, when executed, assigns the element to all provided refs.
 *
 * @example
 * ```tsx
 * const ref1 = useRef<ScrollView>(null);
 * const ref2 = useRef<ScrollView>(null);
 *
 * <ScrollView ref={mergeRefs(ref1, ref2)} />
 * ```
 *
 * When the `ScrollView` mounts, both `ref1.current` and `ref2.current` will point to the same instance.
 */
export function mergeRefs<T>(...refs: (React.Ref<T> | undefined)[]) {
    return (value: T) => {
      refs.forEach((ref) => {
        if (typeof ref === 'function') {
          // Caso ref callback (funzione): chiama la funzione con `value` come argomento
          ref(value);
        } else if (ref && 'current' in ref) {
          // Caso ref oggetto (useRef): assegna il valore all'attributo `current`
          (ref as React.MutableRefObject<T | null>).current = value;
        }
      });
    };
  }
  